import { useRef } from 'react';
import { useChatbotPrompt } from 'queries/chatbot/queries';
import { buildChatMessage } from '../langflow.helper';
import { useLangflowChatContext } from '../contexts/LangflowChat.context';
import { useForm } from 'react-hook-form';

export type Message = {
  id: string;
  sender: 'User' | 'Machine';
  content: string;
  date: string;
};

type LangFlowFormData = {
  input_value: string;
};

export const useLangflowChat = () => {
  const formMethods = useForm<LangFlowFormData>({});
  const {
    appendMessageToChat,
    getChatMessages,
    updateChatSessionId,
    setSelectedSessionId,
    selectedSessionId,
  } = useLangflowChatContext();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const messages = getChatMessages(selectedSessionId);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const { mutate: sendPrompt, isLoading } = useChatbotPrompt({
    onSuccessHandler: (message: Message, sessionId: string) => {
      scrollToBottom();
      if (sessionId !== selectedSessionId) {
        updateChatSessionId({
          oldSessionId: selectedSessionId,
          newSessionId: sessionId,
        });
        setSelectedSessionId(sessionId);
      }
      appendMessageToChat({ message, sessionId });
    },
    onErrorHandler: (message: Message) =>
      appendMessageToChat({ message, sessionId: selectedSessionId }),
  });

  const sendMessage = async (data: LangFlowFormData) => {
    const { input_value } = data;
    if (!input_value || !input_value.trim() || isLoading) return;

    const userMessage = buildChatMessage({ content: input_value });

    appendMessageToChat({ sessionId: selectedSessionId, message: userMessage });

    sendPrompt({
      user_message: input_value,
      session_id: selectedSessionId,
    });

    formMethods.reset({ input_value: '' });
  };

  return {
    messages,
    appendMessageToChat,
    isLoading,
    messagesEndRef,
    sessionId: selectedSessionId,
    scrollToBottom,
    sendMessage: formMethods.handleSubmit(sendMessage),
    messagesContainerRef,
    formMethods,
    onSubmit: formMethods.handleSubmit(sendMessage),
  };
};
